import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, Optional, SkipSelf, ViewChild} from '@angular/core';
import {Location} from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {DragulaService} from 'ng2-dragula';
import {AddQuestionComponent} from './components/add-question/add-question.component';
import {ScoringVariablesComponent} from './components/scoring-variables/scoring-variables.component';
import {NgbConfig, NgbModal, NgbNav, NgbPopover, NgbTooltipConfig} from '@ng-bootstrap/ng-bootstrap';

import {MediaCenter} from '@ngmedax/asset';
import {LayoutService} from '@ngmedax/layout';
import {ConfigService} from '@ngmedax/config';
import {SimuService} from '@ngmedax/simu';
import {Translatable, TranslationEventService, TranslationService} from '@ngmedax/translation';
import {flagIconCodeMap, languages} from '@ngmedax/cloud-translator';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';

import {dragulaScroll, dragulaScrollStop} from './scripts/dragula-scroll.script';
import {QuestionnaireEditorService} from './services/questionnaire-editor.service';
import {QuestionnaireStateService} from './services/questionnaire-state.service';
import {QuestionnaireTabsService} from './services/questionnaire-tabs.service';
import {getEditorLayout} from './services/editor-layout';
import {QuestionnaireMediaCenterService} from './services/questionnaire-media-center.service';
import {DomHelperService} from './services/dom-helper.service';
import {CONTAINER_FORMAT_TO_COMPONENT_MAPPINGS} from '../mappings';
import {FROALA_LICENSE_KEY} from './licences/froala';
import {Filtergroup} from '../types';
import {TRANSLATION_EDITOR_SCOPE} from '../constants';
import {KEYS} from '../translation-keys';
import {QuestionnaireInspectorService} from './services/questionnaire-inspector.service';
import {Question} from '../structure';
import {ApiService} from './services/api.service';
import * as sha from 'js-sha256';
import {VariablesModalComponent} from './components/question/variables/modal/variables-modal.component';
import {QuestionnaireVariablesService} from './services/questionnaire-variables.service';
import {QuestionnaireConditionService} from './services/questionnaire-condition.service';
import {FroalaCodeViewFlushService} from './froala/froala-code-view-flush.service';
import {FormulaValidationService} from './services/formula-validation.service';
import {ToastService} from '../../../toast';

/** Keeps editor tooltips outside its fixed, independently scrolling fullscreen container. */
export function createEditorTooltipConfig(parent: NgbTooltipConfig, config: NgbConfig): NgbTooltipConfig {
  return Object.assign(new NgbTooltipConfig(config), parent, {container: 'body'});
}

/** State of a running resize of the properties panel, sizes are measured once at the start. */
interface InspectorDrag {
  pointerId: number;
  startX: number;
  startWidth: number;
  total: number;
  divider: number;
  layout: HTMLElement;
  target: HTMLElement;
  frame: number;
  clientX: number;
  width: number | null;
}

// hack to inject decorator declarations. must occur before class declaration!
export interface QuestionnaireEditorComponent extends Translatable {}
declare const $: any;

@Component({
  selector: 'app-questionnaire-editor',
  providers: [
    QuestionnaireInspectorService,
    {provide: NgbTooltipConfig, useFactory: createEditorTooltipConfig, deps: [[new SkipSelf(), NgbTooltipConfig], NgbConfig]}
  ],
  templateUrl: './questionnaire-editor.component.html',
  styleUrls: [
    './questionnaire-editor.component.css',
    './shared/reusable.css',
    './shared/insert-element.css'
  ]
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class QuestionnaireEditorComponent implements OnInit, AfterViewInit, OnDestroy {

  /** User-selected inspector width as a percentage of the split layout. */
  public inspectorWidth: number | null = null;
  public paletteCollapsed = false;
  public isResizingInspector = false;
  public inspectorWidthValue = 43;
  private inspectorDrag: InspectorDrag | null = null;

  /** Captures the pointer so resizing continues outside the narrow divider. */
  public startInspectorResize(event: PointerEvent, layout: HTMLElement) {
    if (event.button !== 0 || !event.isPrimary) {
      return;
    }
    event.preventDefault();
    const divider = event.currentTarget as HTMLElement;
    divider.focus({preventScroll: true});
    divider.setPointerCapture(event.pointerId);
    const width = layout.querySelector('.qa-inspector').getBoundingClientRect().width;
    // sizes are measured once; moving only writes the css variable, at most once per frame and outside
    // of change detection, as every width change relayouts the whole document column
    this.inspectorDrag = {
      pointerId: event.pointerId, startX: event.clientX, startWidth: width,
      total: layout.getBoundingClientRect().width, divider: divider.getBoundingClientRect().width,
      layout, target: divider, frame: 0, clientX: event.clientX, width: null
    };
    this.isResizingInspector = true;
    this.updateInspectorWidthValue(layout);
    this.zone.runOutsideAngular(() => divider.addEventListener('pointermove', this.onInspectorPointerMove));
  }

  /** Resizes both columns while keeping each side usable. */
  private readonly onInspectorPointerMove = (event: PointerEvent) => {
    const drag = this.inspectorDrag;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    drag.clientX = event.clientX;
    if (drag.frame) {
      return;
    }
    drag.frame = requestAnimationFrame(() => this.applyInspectorDrag(drag));
  }

  /** Writes the width of the latest pointer position. */
  private applyInspectorDrag(drag: InspectorDrag) {
    drag.frame && cancelAnimationFrame(drag.frame);
    drag.frame = 0;
    const minimum = Math.min(240, (drag.total - drag.divider) / 2);
    const width = drag.startWidth + drag.startX - drag.clientX;
    drag.width = Math.max(minimum, Math.min(width, drag.total - drag.divider - minimum)) / drag.total * 100;
    drag.layout.style.setProperty('--qa-inspector-width', drag.width + '%');
    drag.target.setAttribute('aria-valuenow', String(Math.round(drag.width)));
  }

  /** Ends dragging on release, cancellation, or loss of pointer capture. */
  public endInspectorResize(event: PointerEvent) {
    const drag = this.inspectorDrag;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    drag.target.removeEventListener('pointermove', this.onInspectorPointerMove);
    // a move still waiting for its frame is applied right away, so a quick release keeps it
    drag.frame && this.applyInspectorDrag(drag);
    if (drag.width !== null) {
      this.inspectorWidth = drag.width;
      this.inspectorWidthValue = Math.round(drag.width);
    }
    this.inspectorDrag = null;
    this.isResizingInspector = false;
    const divider = event.currentTarget as HTMLElement;
    if (divider.hasPointerCapture(event.pointerId)) {
      divider.releasePointerCapture(event.pointerId);
    }
  }

  /** Supports arrow keys for resizing and Home for the original split. */
  public resizeInspectorWithKeyboard(event: KeyboardEvent, layout: HTMLElement) {
    if (event.key === 'Home') {
      event.preventDefault();
      this.resetInspectorWidth();
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    event.preventDefault();
    const width = layout.querySelector('.qa-inspector').getBoundingClientRect().width;
    const step = event.shiftKey ? 50 : 20;
    this.setInspectorWidth(width + (event.key === 'ArrowLeft' ? step : -step), layout);
  }

  /** Reports the rendered split to assistive technology when the divider gains focus. */
  public updateInspectorWidthValue(layout: HTMLElement) {
    this.inspectorWidthValue = Math.round(
      layout.querySelector('.qa-inspector').getBoundingClientRect().width / layout.getBoundingClientRect().width * 100
    );
  }

  /** Restores the responsive default without changing questionnaire content. */
  public resetInspectorWidth() {
    this.inspectorWidth = null;
    this.inspectorWidthValue = window.innerWidth <= 1100 ? 48 : 43;
  }

  /** Clamps the width to the available space and retains a proportional split. */
  private setInspectorWidth(width: number, layout: HTMLElement) {
    const total = layout.getBoundingClientRect().width;
    const divider = layout.querySelector('.qa-inspector-divider').getBoundingClientRect().width;
    const minimum = Math.min(240, (total - divider) / 2);
    this.inspectorWidth = Math.max(minimum, Math.min(width, total - divider - minimum)) / total * 100;
    this.inspectorWidthValue = Math.round(this.inspectorWidth);
  }

  /**
   * Default froala wysiwyg editor options
   */
  public defaultEditorOptions: any = {
    key: FROALA_LICENSE_KEY,
    placeholderText: 'Beschreibung eingeben...',
    charCounterCount: true,
    quickInsertButtons: [],
    quickInsertTags: [],
    imagePaste: false,
    pluginsEnabled: ['paragraphFormat', 'charCounter'],
    toolbarButtons: ['bold', 'italic', 'underline', 'paragraphFormat'],
    lineWrapping: true,
    language: 'de',
    attribution: false
  };

  /**
   * Questionnaire data object
   * @type {Questionnaire}
   */
  public questionnaire: Questionnaire;

  /**
   * JSON snapshot of questionnaire. Used to detect if questionnaire
   * contains unsaved content since last init/load/save action
   */
  public questionnaireSnapshot: string;

  /**
   * Memorized unsaved changes for this questionnaire (or a new one), offered in the hint above the editor
   */
  public pendingSession: {id: string, title: string, savedAt: Date} = null;

  /**
   * Questionnaire path hash map. contains all hashes
   * with pointers to elements in the questionnaire
   */
  public pathHashMap: any = {};

  /**
   * Locale for questionnaire. Hardcoded to "de_DE" for now.
   * We need to change this, when we implement multi language support
   * @type {string}
   */
  public locale = 'de_DE';

  /**
   * Mappings from container format to components
   * @type {any}
   */
  public mappings = CONTAINER_FORMAT_TO_COMPONENT_MAPPINGS;

  /**
   * Filtergroups
   *
   * @type {[{}]}
   */
  public filtergroups: Filtergroup[];

  /**
   * Filter groups list gets a search field from this many entries on
   */
  public readonly FILTERGROUP_SEARCH_THRESHOLD = 8;

  /**
   * Search term of the filter group picker
   */
  public filtergroupQuery = '';

  /**
   * Filter group ids in picker order, captured when the picker opens
   */
  private filtergroupOrder: string[] = [];

  /**
   * Storage for questionnaire elements (questions and other elements)
   * @type {Array}
   */
  public elements: any[] = [];

  /**
   * Is the input/output overlay visible?
   * @type {boolean}
   */
  public ioOverlayVisible = false;

  /**
   * Is the translation overlay visible?
   */
  public translationOverlayVisible = false;

  /**
   * Is the ai assistant overlay visible?
   */
  public aiOverlayVisible = false;

  /**
   * Are we in save mode?
   * @type {boolean}
   */
  public saveInProgress = false;

  /**
   * Feature object to determine, what feature is active
   * @type {any}
   */
  public feature: any = {
    editor: {
      patient: {
        upload: false
      }
    },
    pdf: {
      forms: false
    }
  };

  /**
   * Flag to determine if cloud translation is enabled
   * @type {boolean}
   */
  public canCloudTranslate = false;

  /**
   * Is the ai assistant feature unlocked for this user session?
   * @type {boolean}
   */
  public aiAssistantEnabled = false;

  /**
   * Emulated container for meta title (used to set gdt variable)
   * @type {any}
   */
  public metaTitleContainer = {pathHash: sha.sha256('meta.title')};

  /**
   * Flag icon code map
   */
  public flagIconCodeMap = flagIconCodeMap;

  /**
   * Languages
   */
  public languages = languages;

  /**
   * Is single instance multi user mode enabled?
   */
  public isSimu = false;

  /**
   * Page view mode toggle: on by default in the redesign, off in the classic layout like the old editor
   */
  public pageViewEnabled = getEditorLayout(this.configService.get('ui.editor.default')) !== 'classic';

  /**
   * Flag to prevent event handlers from interfering with page operations
   */
  private isPageOperationInProgress = false;

  /**
   * Current page index (0-based)
   */
  public currentPageIndex = 0;

  /**
   * Select2 options for page dropdown
   */
  public pageSelectOptions = {width: '100%', placeholder: '...'};

  /**
   * Cached pages array (rebuilt when questions change)
   */
  public pages: {startIndex: number; endIndex: number; title: string}[] = [];

  /**
   * Cached visible questions array (rebuilt when page changes)
   */
  public visibleQuestions: {question: any; realIndex: number; isFirstOnPage: boolean; isLastOnPage: boolean}[] = [];

  /**
   * Subscriptions that we must unsubscribe from when component gets destroyed
   */
  private subscriptions: Subscription[] = [];

  /**
   * Are we in edit mode?
   * @type {boolean}
   */
  private editMode = false;

  /**
   * Storage for current drag/drop pos
   */
  private dragPos = {old: 0, new: 0};
  @ViewChild('pageNavigation') pageNavigation: ElementRef<HTMLElement>;
  @ViewChild('nav') private metaNavigation: NgbNav;
  @ViewChild('qaMeta') private metaPanel: ElementRef<HTMLElement>;
  @ViewChild('qaMetaTab') private metaTabPanel: ElementRef<HTMLElement>;
  @ViewChild(ScoringVariablesComponent) private scoringVariables: ScoringVariablesComponent;
  private focusScoringAddPending = false;

  /** Reveals questionnaire scoring settings and focuses the add action once its tab is visible. */
  public openScoringVariables(): void {
    if (!this.metaNavigation || !this.metaPanel || !this.metaTabPanel) {
      return;
    }
    this.domHelper.addClasses(this.metaPanel.nativeElement, ['expanded']);
    this.domHelper.addClasses(this.metaTabPanel.nativeElement, ['expanded']);
    this.focusScoringAddPending = true;
    if (this.metaNavigation.activeId === 'scoring') {
      this.onMetaTabShown('scoring');
      return;
    }
    this.metaNavigation.select('scoring');
  }

  /** Waits for the tab transition before moving focus into its rendered content. */
  public onMetaTabShown(tabId: string | number): void {
    if (tabId !== 'scoring' || !this.focusScoringAddPending) {
      return;
    }
    this.ref.detectChanges();
    if (this.scoringVariables) {
      this.focusScoringAddPending = false;
      this.scoringVariables.focusAddButton();
    }
  }

  @ViewChild('elementPalette') elementPalette: AddQuestionComponent;
  public paletteDragging = false;

  /** Opens the element picker before the current page's first element, including empty pages. */
  public openPageStartElementPicker(popover: NgbPopover, event: Event): void {
    event.stopPropagation();
    $('ngb-popover-window').hide();
    popover.close();
    popover.open();
  }

  /** Accept palette copies only in the document, before a page's closing break. */
  public acceptsPaletteDrop(target: Element, sibling: Element | null): boolean {
    if (!target?.classList.contains('qa-palette-drop-target')) {
      return false;
    }
    if (target.classList.contains('qa-document-empty')) {
      return true;
    }
    const page = this.pageViewEnabled && this.pages[this.currentPageIndex];
    const endsWithBreak = page && this.questionnaire.questions[page.endIndex]?.format === 'pageBreak';
    return !endsWithBreak || sibling !== null;
  }

  public insertPaletteDrop(el: Element, target: Element, sibling: Element | null): void {
    const templateName = el.getAttribute('data-question-template');
    const isEmpty = target.classList.contains('qa-document-empty');
    // Remove only Dragula's copy, leaving the palette and Angular's question nodes intact.
    el.parentNode?.removeChild(el);
    const children = Array.from(target.children);
    const localIndex = isEmpty ? 0 : sibling ? children.indexOf(sibling) : children.length;
    if (templateName && localIndex >= 0) {
      this.elementPalette.onAddElement(templateName, this.getPageStartIndex() + localIndex - 1);
      this.ref.markForCheck();
    }
  }


  /**
   * Observer that logs long main thread tasks (> 50ms) for performance analysis
   */
  private longTaskObserver: PerformanceObserver = null;

  /**
   * Cache for the broken formula check (validation compiles, so don't run it on every change detection)
   */
  private brokenFormulaCache: {key: string, names: string[]} = {key: null, names: []};

  /**
   * Injects dependencies, deletes previous dragula element bags and translates froala placeholder text
   */
  public constructor(
    public mediaCenter: QuestionnaireMediaCenterService,
    public tabs: QuestionnaireTabsService,
    public inspector: QuestionnaireInspectorService,
    public domHelper: DomHelperService,
    private editor: QuestionnaireEditorService,
    private state: QuestionnaireStateService,
    private configService: ConfigService,
    private layoutService: LayoutService,
    private simuService: SimuService,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private dragula: DragulaService,
    private ref: ChangeDetectorRef,
    private api: ApiService,
    private modal: NgbModal,
    private toast: ToastService,
    private variables: QuestionnaireVariablesService,
    private conditions: QuestionnaireConditionService,
    private codeViewFlush: FroalaCodeViewFlushService,
    private formulaValidation: FormulaValidationService,
    private zone: NgZone,
    @Optional() private translationService: TranslationService,
    @Optional() private translationEvents: TranslationEventService,
  ) {
    this.canCloudTranslate = this.configService.get('translation.cloud.active') == true;
    this.isSimu = this.simuService.isSimuMode();

    // the ai assistant button only appears when the secret matches and the backend
    // reports the feature as configured and reachable
    if (this.checkAiAssistantUnlocked()) {
      this.api.getAiStatus()
        .then((response: any) => this.aiAssistantEnabled = !!(response && response.active))
        .catch(() => this.aiAssistantEnabled = false);
    }

    /**
     * Bugfix for dragula: Deletes previous elements bag on progressive page reloads
     */
    for (const bag of ['elements-bag', 'images-bag', 'palette-bag']) {
      this.dragula.find(bag) && this.dragula.destroy(bag);
    }

    const updatePlaceholderText = () => this.defaultEditorOptions.placeholderText = this._(KEYS.EDITOR.ENTER_DESCRIPTION);

    this.translationEvents && this.translationEvents.onLocaleChanged().subscribe(() => {
      updatePlaceholderText();
      this.ref.markForCheck();
      this.ref.detectChanges();

      this.defaultEditorOptions.language = this.translationService.getLocaleCountryCode(
        this.translationService.getLocale()).toLowerCase();

      // hack to force update of question translations
      const questions = this.questionnaire.questions;
      this.questionnaire.questions = [];
      setTimeout(() => {
        this.questionnaire.questions = questions;

        if (this.pageViewEnabled) {
          this.rebuildPages();
        }
      }, 100);
    });

    updatePlaceholderText();
  }

  /**
   * Returns true when the ai assistant is unlocked: a secret must be configured
   * (feature.editor.aiAssistant.secret) and the url must contain a matching
   * "aiAssistant" query param. A successful unlock is remembered for the browser
   * session, since query params get lost on in-app navigation.
   */
  private checkAiAssistantUnlocked(): boolean {
    const secret = this.configService.get('feature.editor.aiAssistant.secret');

    if (!secret) {
      return false;
    }

    const storageKey = 'qaAiAssistantSecret';

    if (sessionStorage && sessionStorage.getItem(storageKey) === secret) {
      return true;
    }

    const urlParam = new URLSearchParams(window.location.search).get('aiAssistant');

    if (urlParam === secret) {
      sessionStorage && sessionStorage.setItem(storageKey, secret);
      return true;
    }

    return false;
  }

  /**
   * Initializes some subscriptions, froala wysiwyg editor and dragula dnd
   */
  public ngOnInit() {
    this.subscriptions.push(this.state.onOpenScoringVariables.subscribe(() => this.openScoringVariables()));

    // shows preloader
    this.layoutService.showPreloader();

    // start perf logging for MYM-1029
    this.initLongTaskLogging();

    // initialize custom field definitions for conditions and variable mappings
    this.conditions.initCustomFields();
    this.variables.initCustomFieldMappings();

    // get editor features
    this.feature.editor = this.configService.get('feature.editor') || this.feature.editor;
    this.feature.pdf.forms = this.configService.get('feature.pdf.forms') || this.feature.pdf.forms;

    // load filter groups and change select2 selection on questionnaire loading
    const filtergroupChange = this.state.onSetQuestionnaire.subscribe(async (questionnaire) => {
      this.filtergroups = await this.editor.loadFiltergroups();
    });

    this.subscriptions.push(filtergroupChange);

    if (document && document.location && document.location.pathname && !document.location.pathname.match(/editor\/.*$/)) {
      this.applyRestrictions();
    }

    // init a new questionnaire and link it to the state service
    this.startSession(this.editor.initQuestionnaire(this.pathHashMap));

    // Palette copies use no Dragula model: the existing add flow creates IDs and path hashes.
    this.dragula.createGroup('palette-bag', {
      copy: true,
      copySortSource: false,
      moves: (el) => el.hasAttribute('data-question-template'),
      isContainer: (el) => el.classList.contains('qa-palette-drop-target'),
      accepts: (el, target, source, sibling) => this.acceptsPaletteDrop(target, sibling)
    });
    this.subscriptions.push(this.dragula.drag('palette-bag').subscribe(() => {
      this.paletteDragging = true;
      dragulaScroll();
    }));
    this.subscriptions.push(this.dragula.drop('palette-bag').subscribe(({el, target, sibling}) => {
      if (target) {
        this.insertPaletteDrop(el, target, sibling);
      }
    }));
    this.subscriptions.push(this.dragula.dragend('palette-bag').subscribe(() => {
      this.paletteDragging = false;
      dragulaScrollStop();
      this.ref.markForCheck();
    }));

    // only allow dragging via elements that contain the class: qa-handle-control
    // in page view: prevent dropping after the pageBreak element (last position)
    this.dragula.createGroup('elements-bag', {
      moves: (el, container, handle) => handle.classList.contains('qa-handle-control'),
      accepts: (el, target, source, sibling) => {
        // in page view, don't allow dropping at the end (after pageBreak)
        if (this.pageViewEnabled && sibling === null) {
          return false;
        }
        return true;
      }
    });

    // auto scroll the editor, when dragging an element
    this.subscriptions.push(this.dragula.drag('elements-bag').subscribe((opts) => {
      const domIndex = [...<any>opts.el.parentNode.children].indexOf(opts.el);
      this.dragPos.old = domIndex + this.getPageStartIndex();
      dragulaScroll();
    }));

    // trigger change detection and stop scrolling when element was dropped
    this.subscriptions.push(this.dragula.drop('elements-bag').subscribe((opts) => {
      const domIndex = [...<any>opts.target.children].indexOf(opts.el);
      this.dragPos.new = domIndex + this.getPageStartIndex();
      this.state.changeElementPosition(this.dragPos.old, this.dragPos.new);
      dragulaScrollStop();
    }));

    // stop scrolling if cancel event was triggered
    this.subscriptions.push(this.dragula.cancel('elements-bag').subscribe(dragulaScrollStop));

    // rebuild pages when elements change (only if page view is enabled)
    // skip if a page operation is in progress (addNewPage, copyCurrentPage, deleteCurrentPage handle their own logic)
    this.subscriptions.push(this.state.onAddElement.subscribe((change) => {
      this.inspector.requestedQuestion = change.element;
      if (!this.pageViewEnabled || this.isPageOperationInProgress) {
        return;
      }

      // close the first page immediately, just like pages created with addNewPage
      if (this.questionnaire.questions.length === 1 && change.element.format !== 'pageBreak') {
        this.isPageOperationInProgress = true;
        try {
          this.state.addElement(this.createPageBreakElement());
        } finally {
          this.isPageOperationInProgress = false;
          this.inspector.requestedQuestion = change.element;
        }
      }

      const savedIndex = this.currentPageIndex;
      const oldPagesCount = this.pages.length;
      const currentPage = this.pages[savedIndex];
      const wasInsertedAtEnd = currentPage && change?.position >= currentPage.endIndex;

      this.rebuildPages();

      // when a pageBreak was inserted mid-page, a new page is created before the current one
      const isPageBreakInsert = change?.element?.format === 'pageBreak';
      const newPageCreated = this.pages.length > oldPagesCount;
      const newIndex = (isPageBreakInsert && newPageCreated && !wasInsertedAtEnd) ? savedIndex + 1 : savedIndex;

      this.forceSelectPage(Math.min(newIndex, this.pages.length - 1));
    }));
    this.subscriptions.push(this.state.onDeleteElement.subscribe(() => {
      if (!this.pageViewEnabled || this.isPageOperationInProgress) {
        return;
      }
      const savedIndex = this.currentPageIndex;
      this.rebuildPages();
      this.forceSelectPage(Math.min(savedIndex, this.pages.length - 1));
    }));
    this.subscriptions.push(this.state.onElementPositionChange.subscribe(() => {
      if (!this.pageViewEnabled || this.isPageOperationInProgress) {
        return;
      }
      const savedIndex = this.currentPageIndex;
      this.rebuildPages();
      this.forceSelectPage(savedIndex);
    }));
    this.subscriptions.push(this.state.onUpdateElement.subscribe((change) => {
      if (!this.pageViewEnabled || this.isPageOperationInProgress) {
        return;
      }
      if (change.element?.format === 'pageBreak') {
        const savedIndex = this.currentPageIndex;
        this.rebuildPages();
        this.forceSelectPage(savedIndex);
      }
    }));

    // handle move-to-page event from question components
    this.subscriptions.push(this.state.onMoveToPage.subscribe((targetPageIndex: number) => {
      this.rebuildPages();
      this.forceSelectPage(Math.min(targetPageIndex, this.pages.length - 1), true);
    }));

    /**
     * Load questionnaire by params.id on routing event
     */
    const routeSubscription = this.activatedRoute.params.subscribe(async (params: {id: string}) => {
      await this.loadQuestionnaireById(params.id);
      this.applySimuRestrictions();
      this.checkMemorizedSession(params.id);
    });

    this.subscriptions.push(routeSubscription);
  }

  @ViewChild('qaEditor') private editorFrame: ElementRef<HTMLElement>;
  @ViewChild('workspace') private workspace: ElementRef<HTMLElement>;

  /**
   * Below this viewport width the workspace columns stack and scroll with the page
   */
  private readonly APP_LAYOUT_MIN_WIDTH = 900;

  /**
   * The workspace never shrinks below this height, the page scrolls instead
   */
  private readonly APP_LAYOUT_MIN_HEIGHT = 420;

  /** single column editor of the old administration, the cards show their properties themselves */
  public classicLayout = getEditorLayout(this.configService.get('ui.editor.default')) === 'classic';

  /**
   * Whether the columns scroll on their own; bound in the template so the first render already
   * uses this layout, switching it later relayouts every question of a large questionnaire
   */
  public appLayout = typeof window !== 'undefined' && !this.classicLayout && window.innerWidth > this.APP_LAYOUT_MIN_WIDTH;

  /**
   * Pending animation frame of the workspace fitting
   */
  private workspaceFitFrame = 0;

  /**
   * Refits the workspace when the content above it changes height, e.g. when the settings expand
   */
  private workspaceResizeObserver: ResizeObserver;

  /**
   * Fits the workspace into the viewport, at most once per frame
   */
  private readonly onViewportChange = () => {
    if (this.workspaceFitFrame) {
      return;
    }
    this.workspaceFitFrame = requestAnimationFrame(() => {
      this.workspaceFitFrame = 0;
      this.fitWorkspaceToViewport();
    });
  }

  /**
   * Starts fitting the workspace to the visible viewport height
   */
  public ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      window.addEventListener('resize', this.onViewportChange);
      if (typeof ResizeObserver !== 'undefined') {
        this.workspaceResizeObserver = new ResizeObserver(this.onViewportChange);
        this.workspaceResizeObserver.observe(document.body);
        this.editorFrame && this.workspaceResizeObserver.observe(this.editorFrame.nativeElement);
      }
    });
    this.onViewportChange();
  }

  /**
   * Lets the workspace fill the remaining viewport height, so palette, document and properties
   * scroll on their own and share the same top edge. Narrow screens keep the stacked page layout.
   */
  private fitWorkspaceToViewport(): void {
    const workspace = this.workspace && this.workspace.nativeElement;
    if (!workspace) {
      return;
    }

    const appLayout = !this.classicLayout && window.innerWidth > this.APP_LAYOUT_MIN_WIDTH;
    this.appLayout = appLayout;
    workspace.classList.toggle('qa-app-layout', appLayout);
    if (!appLayout) {
      workspace.style.height = '';
      return;
    }

    const frame = this.editorFrame && this.editorFrame.nativeElement;
    const maximized = !!frame && frame.classList.contains('maximized');
    // in fullscreen the editor frame scrolls instead of the page
    const scroller = maximized ? frame : document.documentElement;
    const scrollerTop = maximized ? frame.getBoundingClientRect().top + frame.clientTop : 0;
    const top = workspace.getBoundingClientRect().top - scrollerTop + scroller.scrollTop;

    // spacing of the surrounding containers below the workspace, so the page itself does not scroll
    let below = 0;
    for (let element: HTMLElement = workspace; element && element !== scroller && element !== document.body; element = element.parentElement) {
      const style = getComputedStyle(element);
      below += parseFloat(style.marginBottom) || 0;
      if (element !== workspace) {
        below += (parseFloat(style.paddingBottom) || 0) + (parseFloat(style.borderBottomWidth) || 0);
      }
    }
    if (maximized) {
      below += parseFloat(getComputedStyle(frame).paddingBottom) || 0;
    }

    const height = Math.max(this.APP_LAYOUT_MIN_HEIGHT, Math.floor(scroller.clientHeight - top - below));
    workspace.style.height = `${height}px`;
  }

  /**
   * Unsubscribe from all subscriptions
   */
  public ngOnDestroy() {
    this.memorizeSession();

    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }

    this.dragula.find('palette-bag') && this.dragula.destroy('palette-bag');
    dragulaScrollStop();
    window.removeEventListener('resize', this.onViewportChange);
    this.workspaceFitFrame && cancelAnimationFrame(this.workspaceFitFrame);
    this.workspaceResizeObserver && this.workspaceResizeObserver.disconnect();
    this.longTaskObserver && this.longTaskObserver.disconnect();
  }

  public getLangCodes(): string[] {
    if (this.questionnaire && this.questionnaire.meta && this.questionnaire.meta.title) {
      return Object.keys(this.questionnaire.meta.title).filter(locale => this.languages[locale]);
    }
    return [];
  }

  public translateLanguage(language: string): string {
    return this.translationService ?
      this.translationService.translate({
        text: language,
        scope: ['cloudTranslatorModule', 'default']
      }) : language;
  }

  /**
   * Memorizes unsaved changes on page leave, they are offered again when the editor opens.
   * No leave confirmation: nothing gets lost, neither here nor when navigating inside the app (ngOnDestroy)
   */
  @HostListener('window:beforeunload')
  memorizeQuestionnaireOnPageLeave() {
    this.memorizeSession();
  }

  /**
   * Trigger save on Ctrl/Cmd + S.
   * Skips questionnaire save when a modal is open, as modals handle Ctrl+S themselves.
   */
  @HostListener('window:keydown', ['$event'])
  onGlobalSaveShortcut(event: KeyboardEvent) {
    if ((event.ctrlKey || event.metaKey) && (event.key === 's' || event.key === 'S')) {
      event.preventDefault();

      if (this.modal.hasOpenModals()) {
        return;
      }

      if (!this.saveInProgress) {
        this.onSaveQuestionnaire();
      }
    }
  }

  /**
   * Loads questionnaire by given id
   *
   * @param id
   */
  public async loadQuestionnaireById(id: string) {
    if (!id) {
      // hides preloader
      this.layoutService.hidePreloader();
      return;
    }

    // id given, so we are in edit mode
    this.editMode = true;

    // shows preloader
    this.layoutService.showPreloader();

    try {
      const loadStart = performance.now();
      this.startSession(await this.editor.loadQuestionnaire(id, this.pathHashMap));
      this.logRenderPerf(loadStart);
      this.layoutService.hidePreloader();
      this.notifyOnBrokenFormulas();
    } catch (error) {
      alert(this._(KEYS.EDITOR.ERROR_LOADING_QUESTIONNAIRE));
      console.log(error);
    }
  }

  /**
   * Logs load and initial render time of the questionnaire to the console.
   * Waits two animation frames before measuring the render time, so that
   * component creation, froala initialization and the first paint are included.
   * Perf logging for MYM-1029, helps to compare timings before/after optimizations.
   *
   * @param {number} loadStart Timestamp taken before the questionnaire was loaded
   */
  private logRenderPerf(loadStart: number) {
    const loadEnd = performance.now();
    const numElements = (this.questionnaire && this.questionnaire.questions) ? this.questionnaire.questions.length : 0;
    console.log(`[qa-perf] load total: ${Math.round(loadEnd - loadStart)}ms (${numElements} elements)`);

    // static lazy froala previews reuse the fr-box class for styling,
    // so only count boxes with a real (contenteditable) froala instance
    const realEditorSelector = '.fr-box:not(.qa-lazy-froala-static)';

    requestAnimationFrame(() => requestAnimationFrame(() => {
      const numEditors = document.querySelectorAll(realEditorSelector).length;
      const numPreviews = document.querySelectorAll('.qa-lazy-froala-static').length;
      console.log(`[qa-perf] initial render: ${Math.round(performance.now() - loadEnd)}ms, `
        + `froala editors: ${numEditors}, lazy previews: ${numPreviews}`);
    }));

    // delayed measurements to see how many question components and froala editors
    // exist once the editor has settled after the initial load
    for (const delay of [5000, 15000]) {
      setTimeout(() => {
        const numQuestions = document.querySelectorAll('.qa-question').length;
        const numEditors = document.querySelectorAll(realEditorSelector).length;
        const numPreviews = document.querySelectorAll('.qa-lazy-froala-static').length;
        console.log(`[qa-perf] after ${delay / 1000}s: ${numQuestions} question components, `
          + `${numEditors} froala editors, ${numPreviews} lazy previews`);
      }, delay);
    }
  }

  /**
   * Starts logging of long main thread tasks (> 50ms) to the console.
   * Useful to measure editor responsiveness (e.g. while typing or hovering).
   * Perf logging for MYM-1029, helps to compare timings before/after optimizations.
   */
  private initLongTaskLogging() {
    try {
      this.longTaskObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          console.log(`[qa-perf] long task: ${Math.round(entry.duration)}ms`);
        }
      });
      this.longTaskObserver.observe({entryTypes: ['longtask']});
    } catch (error) {
      // long task api not supported by this browser. perf logging is optional, so we ignore this
      this.longTaskObserver = null;
    }
  }

  /**
   * Event handler for when we should save the questionnaire
   */
  public async onSaveQuestionnaire() {
    await this.codeViewFlush.flushAll();

    if (!this.questionnaire || !this.questionnaire.meta || !this.questionnaire.meta.title[this.locale]) {
      alert(this._(KEYS.EDITOR.PLEASE_INSERT_TITLE_TO_SAVE));
      return;
    }

    const hasNoFilterGroups = !this.filterTags(this.questionnaire.meta.tags || []).length;
    if (this.isSimu && hasNoFilterGroups) {
      alert(this._(KEYS.EDITOR.PLEASE_INSERT_FILTER_GROUP_TO_SAVE));
      return;
    }

    this.saveInProgress = true;

    try {
      await this.editor.saveQuestionnaire(this.editMode);

      /**
       * NOTE: The components can alter the questionnaire when rendering it. Therefore we have to wait a moment.
       * otherwise the "is dirty" check might not work correctly...
       */
      this.questionnaire = await this.editor.loadQuestionnaire(this.questionnaire.id, this.pathHashMap);
      await new Promise(resolve => setTimeout(resolve, 200));

      this.startSession(this.questionnaire);
      this.editMode = true;
      this.saveInProgress = false;
      alert(this._(KEYS.EDITOR.SUCCESSFULLY_SAVED_QUESTIONNAIRE));
    } catch (error) {
      this.saveInProgress = false;
      alert(this._(KEYS.EDITOR.ERROR_SAVING_QUESTIONNAIRE));
      console.error(error);
    }
  }

  /**
   * Returns names of formula variables whose expression does not validate.
   * Cached by the questionnaire variables, as this runs from the template
   *
   * @returns {string[]}
   */
  public getBrokenFormulaNames(): string[] {
    const variables: any = (this.questionnaire && this.questionnaire.meta && this.questionnaire.meta.options
      && this.questionnaire.meta.options.variables) || {};
    const formulaVars: any = variables['formula'] || {};
    const key = JSON.stringify(variables);

    if (this.brokenFormulaCache.key === key) {
      return this.brokenFormulaCache.names;
    }

    // single formula problems plus problems over all formulas (formulas using each other or a broken formula)
    const usage = this.formulaValidation.validateFormulaUsage(this.questionnaire);
    const names = Object.keys(formulaVars).filter(name => {
      const expression = (formulaVars[name] && formulaVars[name].expression) || '';
      return !!this.formulaValidation.validate(expression + '', this.questionnaire) || !!usage[name];
    });

    this.brokenFormulaCache = {key, names};
    return names;
  }

  /**
   * Returns true when the questionnaire contains broken formulas (marks the formula tab)
   *
   * @returns {boolean}
   */
  public hasBrokenFormulas(): boolean {
    return this.getBrokenFormulaNames().length > 0;
  }

  /**
   * Shows a toast when the loaded questionnaire contains broken formulas
   */
  private notifyOnBrokenFormulas() {
    const brokenNames = this.getBrokenFormulaNames();

    if (brokenNames.length) {
      this.toast.error(`${this._(KEYS.EDITOR.FORMULA_BROKEN_TOAST)}: ${brokenNames.join(', ')}`);
    }
  }

  /**
   * Opens media center modal
   *
   * @param mediaType
   * @param containerAsset
   */
  public onOpenMediaCenterModal(containerAsset: Questionnaire.Container.Asset = null) {
    const locale = this.locale;
    const questionnaireId =  this.state.getQuestionnaire().id;
    const buildAsset = this.mediaCenter.buildContainerAsset;
    const maintainAspectRatio = true;
    const aspectRatio = 16 / 3.8;

    const callback = (mediaCenterAsset: MediaCenter.Asset) => {
      containerAsset = buildAsset({mediaCenterAsset, containerAsset, locale});
      this.questionnaire.meta.asset.image = containerAsset;
    };

    this.mediaCenter.openMediaCenter({
      questionnaireId, mediaType: 'images', locale, containerAsset, callback, maintainAspectRatio, aspectRatio
    });
  }

  /**
   * Deleted meta asset image object of this questionnaire
   */
  public onDeleteImage() {
    if (!this.questionnaire.meta.asset.image) {
      return;
    }

    this.editor.confirmDelete(() => delete(this.questionnaire.meta.asset.image));
  }

  /**
   * Jumps to question
   *
   * @param popover
   */
  public onJumpToQuestion(popover: NgbPopover) {
    $('ngb-popover-window').hide();

    // wait a moment, so we don't hide the new popover
    setTimeout(() => {
      popover.close();
      popover.autoClose = false;
      popover.open();
    }, 100);
  }

  /**
   * Scrolls to an element; in the page view the page holding it is opened first
   *
   * @param {number} index position of the element in the questionnaire
   */
  public jumpToQuestion(index: number): void {
    const question = this.questionnaire && this.questionnaire.questions[index];
    if (!question) {
      return;
    }

    if (this.pageViewEnabled) {
      const pageIndex = this.pages.findIndex(page => index >= page.startIndex && index <= page.endIndex);
      if (pageIndex !== -1 && pageIndex !== this.currentPageIndex) {
        this.forceSelectPage(pageIndex, true);
        this.ref.detectChanges();
      }
    }

    setTimeout(() => {
      const element = document.getElementById(question.id);
      if (!element) {
        return;
      }
      // scrollIntoView would also scroll the maximized editor frame and push the header out of view
      const pane = this.getDocumentScrollPane();
      const offset = () => pane
        ? element.getBoundingClientRect().top - pane.getBoundingClientRect().top - 16
        : element.getBoundingClientRect().top - (parseFloat(getComputedStyle(element).scrollMarginTop) || 0);
      pane ? pane.scrollTo({top: Math.max(0, pane.scrollTop + offset()), behavior: 'smooth'})
        : element.scrollIntoView({block: 'start', behavior: 'smooth'});
      this.correctJumpPosition(pane, offset);
    });
  }

  /**
   * Cards outside the view are only laid out with an estimated height (content-visibility), so the target
   * can move while the cards passed on the way render. Once scrolling settles, the remaining offset is corrected.
   */
  private correctJumpPosition(pane: HTMLElement | null, offset: () => number, attempt = 0): void {
    const scroller: EventTarget = pane || window;
    let timer = 0;
    const settle = () => {
      scroller.removeEventListener('scrollend', settle);
      clearTimeout(timer);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const delta = offset();
        if (Math.abs(delta) <= 2 || attempt >= 3) {
          return;
        }
        pane ? pane.scrollBy({top: delta}) : window.scrollBy({top: delta});
        this.correctJumpPosition(pane, offset, attempt + 1);
      }));
    };
    scroller.addEventListener('scrollend', settle);
    // browsers without scrollend, or no scrolling at all
    timer = window.setTimeout(settle, attempt ? 150 : 900);
  }

  /**
   * Opens modal for gdt meta title variables
   */
  public onGdtMetaTitleVariablesModal() {
    const modalRef = this.modal.open(VariablesModalComponent, {size: 'lg'});
    modalRef.componentInstance.container = this.metaTitleContainer;
    modalRef.componentInstance.disabledScopes = {upload: true, inline: true, pdf: true, client: true, fhir: true, 'pdf-form': true};
  }


  /**
   * Opens modal for gdt meta formula variables
   *
   * @param {string} formulaVarName
   */
  public onGdtMetaFormulaVariablesModal(formulaVarName: string) {
    const virtualContainer = {pathHash: sha.sha256(`formula.${formulaVarName}`)};
    const modalRef = this.modal.open(VariablesModalComponent, {size: 'lg'});
    modalRef.componentInstance.container = virtualContainer;
    modalRef.componentInstance.disabledScopes = {upload: true, inline: true, pdf: true, client: true, fhir: true, 'pdf-form': true};
  }

  /**
   * Adds default gdt formula variables, if not already added.
   * Uses gdt key 8480 by default (result text)
   *
   */
  public addDefaultGdtFormulaVariables() {
    if (!this.questionnaire.meta.options.gdt || !this.questionnaire.meta.options.gdt.addFormulaResults) {
      return;
    }

    const formulaPathHashes = Object.keys(this.questionnaire.meta.options.variables['formula'] || {}).map(varName => sha.sha256(`formula.${varName}`));

    if (!formulaPathHashes.length) {
      return;
    }

    const gdtVariables = this.variables.getVariablesForScope('gdt');
    const flipped = this.variables.flipVariables(gdtVariables);

    for (const formulaPathHash of formulaPathHashes) {
      if (flipped[formulaPathHash]) {
        continue;
      }

      this.variables.addVariableMapping('gdt', '8480', formulaPathHash, {});
    }
  }

  /**
   * Returns true when questionnaire contains formula variables
   * @returns {boolean}
   */
  public hasFormulaVariables(): boolean {
    const formula = this.questionnaire.meta.options.variables['formula'];
    return formula && Object.keys(formula).length > 0
  }

  /**
   * Returns formula variables as array
   * @returns {string[]}
   */
  public getFormulaVariables(): string[] {
    return Object.keys(this.questionnaire.meta.options.variables['formula'] || {});
  }

  /**
   * Opens modal for gdt meta scoring variables
   *
   * @param {string} scoringVarName
   */
  public onGdtMetaScoringVariablesModal(scoringVarName: string) {
    const virtualContainer = {pathHash: sha.sha256(scoringVarName)};
    const modalRef = this.modal.open(VariablesModalComponent, {size: 'lg'});
    modalRef.componentInstance.container = virtualContainer;
    modalRef.componentInstance.disabledScopes = {upload: true, inline: true, pdf: true, client: true, fhir: true, 'pdf-form': true};
  }

  /**
   * Adds default gdt scoring variables, if not already added.
   * Uses gdt key 8480 by default (result text)
   *
   */
  public addDefaultGdtScoringVariables() {
    if (!this.questionnaire.meta.options.gdt || !this.questionnaire.meta.options.gdt.addScoring) {
      return;
    }

    const scoringPathHashes = Object.keys(this.questionnaire.meta.options.variables['scoring'] || {}).map(varName => sha.sha256(varName));

    if (!scoringPathHashes.length) {
      return;
    }

    const gdtVariables = this.variables.getVariablesForScope('gdt');
    const flipped = this.variables.flipVariables(gdtVariables);

    for (const scoringPathHash of scoringPathHashes) {
      if (flipped[scoringPathHash]) {
        continue;
      }

      this.variables.addVariableMapping('gdt', '8480', scoringPathHash, {});
    }
  }

  /**
   * Returns true when questionnaire contains scoring variables
   * @returns {boolean}
   */
  public hasScoring(): boolean {
    const scoring = this.questionnaire.meta.options.variables['scoring'];
    return scoring && Object.keys(scoring).length > 0
  }

  /**
   * Returns scoring variables as array
   * @returns {string[]}
   */
  public getScoringVariables(): string[] {
    return Object.keys(this.questionnaire.meta.options.variables['scoring'] || {});
  }

  /**
   * Filters out non existing filter groups
   *
   * @returns {string[]}
   */
  public filterTags(tags: string[]): string[] {
    const filtered = [];
    const filtergroups = this.filtergroups ? this.filtergroups.map(fg => fg.id) : tags;

    for (const tag of tags) {
      if (filtergroups.indexOf(tag) !== -1) {
        filtered.push(tag);
      }
    }

    return filtered;
  }

  /**
   * Returns whether the given filter group is assigned to the questionnaire
   *
   * @param {string} id
   * @returns {boolean}
   */
  public hasFiltergroup(id: string): boolean {
    return (this.questionnaire.meta.tags || []).indexOf(id) !== -1;
  }

  /**
   * Assigns or removes the given filter group
   *
   * @param {string} id
   */
  public toggleFiltergroup(id: string): void {
    const tags = this.questionnaire.meta.tags || [];
    this.questionnaire.meta.tags = this.hasFiltergroup(id) ? tags.filter(tag => tag !== id) : [...tags, id];
  }

  /**
   * Opens or closes the filter group picker. Other popovers are hidden by a document click handler,
   * so the picker opens after that handler has run.
   *
   * @param {NgbPopover} popover
   */
  public toggleFiltergroupPicker(popover: NgbPopover): void {
    if (popover.isOpen() && $('.qa-filtergroup-popover').is(':visible')) {
      popover.close();
      return;
    }

    this.filtergroupQuery = '';
    this.filtergroupOrder = this.sortFiltergroupsBySelection();

    $('ngb-popover-window').hide();
    setTimeout(() => {
      popover.close();
      popover.open();
      setTimeout(() => {
        const search = document.querySelector<HTMLInputElement>('.qa-filtergroup-search input');
        search && search.focus();
      }, 50);
    }, 100);
  }

  /**
   * Returns the filter groups shown in the picker: assigned groups first, narrowed by the search term
   *
   * @returns {Filtergroup[]}
   */
  public getFiltergroupOptions(): Filtergroup[] {
    const query = this.filtergroupQuery.trim().toLowerCase();
    const position = (filtergroup: Filtergroup) => {
      const index = this.filtergroupOrder.indexOf(filtergroup.id);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };

    return (this.filtergroups || [])
      .filter(filtergroup => !query || (filtergroup.name[this.locale] || '').toLowerCase().indexOf(query) !== -1)
      .sort((a, b) => position(a) - position(b));
  }

  /**
   * Returns all filter group ids with the assigned ones first, keeping the original order otherwise
   *
   * @returns {string[]}
   */
  private sortFiltergroupsBySelection(): string[] {
    const ids = (this.filtergroups || []).map(filtergroup => filtergroup.id);
    return [...ids.filter(id => this.hasFiltergroup(id)), ...ids.filter(id => !this.hasFiltergroup(id))];
  }

  /**
   * Returns the names of all assigned filter groups as a readable list
   *
   * @returns {string}
   */
  public getFiltergroupSummary(): string {
    return (this.filtergroups || [])
      .filter(filtergroup => this.hasFiltergroup(filtergroup.id))
      .map(filtergroup => filtergroup.name[this.locale])
      .join(', ');
  }

  /**
   * Toggles page view mode on/off and resets to first page
   */
  public togglePageView(): void {
    this.pageViewEnabled = !this.pageViewEnabled;
    this.currentPageIndex = 0;

    if (this.pageViewEnabled) {
      this.rebuildPages();
    }
  }

  /**
   * Rebuilds the cached pages array based on pageBreak elements.
   * Call this when questions are added, removed, or reordered.
   */
  public rebuildPages(): void {
    this.pages = [];

    if (!this.questionnaire || !this.questionnaire.questions) {
      return;
    }

    const questions = this.questionnaire.questions;
    let startIndex = 0;

    for (let i = 0; i < questions.length; i++) {
      if (questions[i].format === 'pageBreak') {
        const title = questions[i].title?.[this.locale] || '';
        this.pages.push({startIndex, endIndex: i, title});
        startIndex = i + 1;
      }
    }

    // add last page if there are remaining questions after last pageBreak
    if (startIndex < questions.length) {
      this.pages.push({startIndex, endIndex: questions.length - 1, title: ''});
    }

    // ensure currentPageIndex is still valid
    if (this.currentPageIndex >= this.pages.length && this.pages.length > 0) {
      this.currentPageIndex = this.pages.length - 1;
    }

    this.rebuildVisibleQuestions();
  }

  /**
   * Rebuilds the cached visible questions array.
   * Only needed when page view is enabled.
   */
  public rebuildVisibleQuestions(): void {
    this.visibleQuestions = [];

    // in flat view, we iterate directly over questionnaire.questions in the template
    if (!this.pageViewEnabled) {
      return;
    }

    if (!this.questionnaire || !this.questionnaire.questions || this.pages.length === 0) {
      return;
    }

    if (this.currentPageIndex < 0 || this.currentPageIndex >= this.pages.length) {
      this.currentPageIndex = Math.max(0, Math.min(this.currentPageIndex, this.pages.length - 1));
    }

    const questions = this.questionnaire.questions;
    const currentPage = this.pages[this.currentPageIndex];

    if (!currentPage) {
      return;
    }

    for (let i = currentPage.startIndex; i <= currentPage.endIndex; i++) {
      const question = questions[i];
      const isFirst = i === currentPage.startIndex;
      const isLast = i === currentPage.endIndex;
      const isPageBreak = question.format === 'pageBreak';
      const nextIsPageBreak = i < currentPage.endIndex && questions[i + 1]?.format === 'pageBreak';

      this.visibleQuestions.push({
        question,
        realIndex: i,
        isFirstOnPage: isFirst,
        isLastOnPage: isLast || isPageBreak || nextIsPageBreak
      });
    }
  }

  /**
   * Handles page change from select dropdown
   *
   * @param pageIndex The new page index (as string from select2)
   */
  public onPageChange(pageIndex: string | number): void {
    this.currentPageIndex = typeof pageIndex === 'string' ? parseInt(pageIndex, 10) : pageIndex;
    this.rebuildVisibleQuestions();
    this.resetDocumentScroll();
  }

  /**
   * Forces Select2 to update by changing the index in the next tick.
   * This avoids Angular's ExpressionChangedAfterItHasBeenCheckedError.
   *
   * @param targetIndex The page index to select
   * @param rebuildVisible Whether to rebuild visible questions after selection
   */
  private forceSelectPage(targetIndex: number, rebuildVisible = false): void {
    this.currentPageIndex = targetIndex;
    if (rebuildVisible) {
      this.rebuildVisibleQuestions();
    }
  }

  /**
   * Navigates to next or previous page
   *
   * @param delta Direction: 1 for next, -1 for previous
   */
  public goToPage(delta: number): void {
    if (!this.canGoToPage(delta)) {
      return;
    }

    this.currentPageIndex = this.currentPageIndex + delta;
    this.rebuildVisibleQuestions();
    this.resetDocumentScroll();
  }

  public scrollToPageStart(): void {
    // Render the new page before scrolling, also within the maximized editor.
    this.ref.detectChanges();
    if (this.resetDocumentScroll()) {
      return;
    }
    this.pageNavigation?.nativeElement.scrollIntoView({block: 'start', behavior: 'smooth'});
  }

  /**
   * Starts a newly shown page at its first question when only the questions scroll (app layout)
   *
   * @returns {boolean} whether the questions have their own scroll area
   */
  private resetDocumentScroll(): boolean {
    const pane = this.getDocumentScrollPane();
    if (!pane) {
      return false;
    }
    pane.scrollTop = 0;
    return true;
  }

  /**
   * Label of the drop marker while dragging an element, as a css string for the marker's content
   */
  public get dropLabel(): string {
    return JSON.stringify('+  ' + this._(KEYS.EDITOR.INSERT_ELEMENT_HERE));
  }

  /**
   * Returns the own scroll area of the questions, which only exists in app layout
   */
  private getDocumentScrollPane(): HTMLElement | null {
    const workspace = this.workspace && this.workspace.nativeElement;
    return workspace && workspace.classList.contains('qa-app-layout')
      ? workspace.querySelector<HTMLElement>('.qa-document-scroll')
      : null;
  }

  /**
   * Checks if navigation to next/previous page is possible
   *
   * @param delta Direction: 1 for next, -1 for previous
   * @returns True if navigation is possible
   */
  public canGoToPage(delta: number): boolean {
    const newIndex = this.currentPageIndex + delta;
    return newIndex >= 0 && newIndex < this.pages.length;
  }

  /**
   * TrackBy function for ngFor to preserve DOM elements and scroll position.
   *
   * @param index The index in the array
   * @param item The visible question item
   * @returns The question id for tracking
   */
  public trackByQuestionId(index: number, item: {question: any; realIndex: number}): string {
    return item.question.id;
  }

  /**
   * TrackBy function for flat view (direct question iteration).
   *
   * @param index The index in the array
   * @param question The question container
   * @returns The question id for tracking
   */
  public trackByQuestion(index: number, question: Questionnaire.Container): string {
    return question.id;
  }

  /**
   * Returns the start index of the current page.
   * Used to map DOM indices to real questionnaire indices when in page view mode.
   *
   * @returns Start index of current page, or 0 if page view is disabled
   */
  public getPageStartIndex(): number {
    if (!this.pageViewEnabled || this.pages.length === 0) {
      return 0;
    }

    if (this.currentPageIndex >= this.pages.length) {
      return 0;
    }

    return this.pages[this.currentPageIndex].startIndex;
  }

  /** Page view: the page break closing the current page holds its title, the title field above the questions edits it */
  public get currentPageBreak(): any {
    const page = this.pageViewEnabled && this.pages[this.currentPageIndex];
    const element = page && this.questionnaire?.questions[page.endIndex];
    return element?.format === 'pageBreak' ? element : null;
  }

  private pageTitleTimeout: any = null;

  public setPageTitle(title: string): void {
    const pageBreak = this.currentPageBreak;
    if (!pageBreak) {
      return;
    }
    const page = this.pages[this.currentPageIndex];
    pageBreak.title = {...pageBreak.title, [this.locale]: title};
    page.title = title;
    clearTimeout(this.pageTitleTimeout);
    this.pageTitleTimeout = setTimeout(() => this.state.updateElement(pageBreak, page.endIndex), 300);
  }

  /**
   * Adds a new empty page after the current page.
   * If the current page has no pageBreak, one will be created first.
   */
  public addNewPage(): void {
    if (!this.pageViewEnabled || this.pages.length === 0) {
      return;
    }

    this.isPageOperationInProgress = true;
    const targetPageIndex = this.currentPageIndex + 1;

    const currentPage = this.pages[this.currentPageIndex];
    const questions = this.questionnaire.questions;
    const lastQuestion = questions[currentPage.endIndex];
    const hasPageBreak = lastQuestion?.format === 'pageBreak';

    // if current page has no pageBreak, create one first
    if (!hasPageBreak) {
      const newPageBreak = this.createPageBreakElement();
      this.state.addElement(newPageBreak, currentPage.endIndex);
      this.rebuildPages();
    }

    // show loader
    this.layoutService.showPreloader();

    // get updated page reference after potential rebuild
    const updatedPage = this.pages[this.currentPageIndex];

    // insert a new pageBreak after the current page's pageBreak
    const newPageBreak = this.createPageBreakElement();
    this.state.addElement(newPageBreak, updatedPage.endIndex);

    // rebuild pages and navigate to the new page
    this.rebuildPages();
    this.currentPageIndex = targetPageIndex;
    this.rebuildVisibleQuestions();

    // hide loader after 500ms and show success message
    setTimeout(() => {
      this.layoutService.hidePreloader();
      this.isPageOperationInProgress = false;
      alert(this._(KEYS.EDITOR.PAGE_ADDED_SUCCESSFULLY));
    }, 500);
  }

  /**
   * Copies the current page including all containers.
   * If the current page has no pageBreak, one will be created first.
   * The copied containers are inserted after the current page's pageBreak.
   */
  public copyCurrentPage(): void {
    if (!this.pageViewEnabled || this.pages.length === 0) {
      return;
    }

    this.isPageOperationInProgress = true;
    const targetPageIndex = this.currentPageIndex + 1;

    const currentPage = this.pages[this.currentPageIndex];
    const questions = this.questionnaire.questions;
    const lastQuestion = questions[currentPage.endIndex];
    const hasPageBreak = lastQuestion?.format === 'pageBreak';

    // if current page has no pageBreak, create one first
    if (!hasPageBreak) {
      const newPageBreak = this.createPageBreakElement();
      this.state.addElement(newPageBreak, currentPage.endIndex);
      this.rebuildPages();
    }

    // show loader
    this.layoutService.showPreloader();

    // get updated page reference after potential rebuild
    const updatedPage = this.pages[this.currentPageIndex];

    // collect all containers of current page
    const containersToCopy: any[] = [];
    for (let i = updatedPage.startIndex; i <= updatedPage.endIndex; i++) {
      containersToCopy.push(questions[i]);
    }

    // duplicate containers via state service. duplicateElement regenerates the
    // top-level uuid, recomputes path hashes, copies mapped variables and remaps
    // condition references so the copy is fully isolated from the source.
    const copiedContainers = containersToCopy.map(container => this.state.duplicateElement(container));

    // insert copied containers after the current page's pageBreak (in reverse order)
    const insertPosition = updatedPage.endIndex;
    for (let i = copiedContainers.length - 1; i >= 0; i--) {
      this.state.addElement(copiedContainers[i], insertPosition);
    }

    // rebuild pages and navigate to the new page
    this.rebuildPages();
    this.currentPageIndex = targetPageIndex;
    this.rebuildVisibleQuestions();

    // hide loader after 1 second and show success message
    setTimeout(() => {
      this.layoutService.hidePreloader();
      this.isPageOperationInProgress = false;
      alert(this._(KEYS.EDITOR.PAGE_COPIED_SUCCESSFULLY));
    }, 1000);
  }

  /**
   * Deletes the current page including all its containers.
   * Asks for confirmation before deleting.
   */
  public async deleteCurrentPage(): Promise<void> {
    if (!this.pageViewEnabled || this.pages.length === 0) {
      return;
    }

    // ask for confirmation
    if (!await confirm(this._(KEYS.EDITOR.CONFIRM_DELETE_PAGE))) {
      return;
    }

    this.isPageOperationInProgress = true;

    // show loader
    this.layoutService.showPreloader();

    const currentPage = this.pages[this.currentPageIndex];
    const targetPageIndex = Math.max(0, this.currentPageIndex - 1);

    // delete all containers of the current page (from end to start to preserve indices)
    for (let i = currentPage.endIndex; i >= currentPage.startIndex; i--) {
      this.state.deleteElement(i);
    }

    // rebuild pages and navigate to the appropriate page
    this.rebuildPages();
    this.currentPageIndex = Math.min(targetPageIndex, this.pages.length - 1);
    this.rebuildVisibleQuestions();

    // hide loader after 500ms and show success message
    setTimeout(() => {
      this.layoutService.hidePreloader();
      this.isPageOperationInProgress = false;
      alert(this._(KEYS.EDITOR.PAGE_DELETED_SUCCESSFULLY));
    }, 500);
  }

  /**
   * Creates a new pageBreak element with a new UUID.
   *
   * @returns A new pageBreak container
   */
  private createPageBreakElement(): Questionnaire.Container {
    const pageBreak = JSON.parse(JSON.stringify(Question.Structure.PAGE_BREAK));
    pageBreak.id = this.editor.generateUUID();
    pageBreak.title = {[this.locale]: ''};
    return pageBreak;
  }

  /**
   * Starts new questionnaire session. Creates snapshot, so we can detect
   * if questionnaire contains unsaved changes
   *
   * @param {Questionnaire} questionnaire
   */
  private startSession(questionnaire: Questionnaire) {
    this.questionnaire = questionnaire;
    this.questionnaireSnapshot = this.sessionFingerprint(questionnaire);

    if (this.pageViewEnabled) {
      this.rebuildPages();
    }
  }

  /**
   * Returns true if questionnaire contains unsaved content
   *
   * @returns {boolean}
   */
  private isDirty() {
    const current = this.sessionFingerprint(this.questionnaire);
    return current != this.questionnaireSnapshot;
  }

  /**
   * Serialized questionnaire for the dirty check. Without the schema version: the state service sets it on every
   * read, so an opened older questionnaire would count as changed although nobody touched it. Empty objects are
   * left out as well: the variable settings create empty scopes (e.g. "extern") when they are opened
   *
   * @param {Questionnaire} questionnaire
   * @returns {string}
   */
  private sessionFingerprint(questionnaire: Questionnaire): string {
    const prune = (value: any): any => {
      if (!value || typeof value !== 'object') { return value; }
      if (typeof value.toJSON === 'function') { return value.toJSON(); }
      if (Array.isArray(value)) { return value.map(prune); }
      const result = {};
      Object.keys(value).forEach(key => {
        const pruned = prune(value[key]);
        const isEmpty = pruned && typeof pruned === 'object' && !Array.isArray(pruned) && !Object.keys(pruned).length;
        !isEmpty && (result[key] = pruned);
      });
      return result;
    };
    return JSON.stringify(prune({...questionnaire, softwareVersion: undefined}));
  }

  /**
   * Memorizes current questionnaire, it has questions and contains unsaved content
   */
  private memorizeSession() {
    // memorize questionnaire, if it has questions and contains unsaved content
    const isDirty = this.questionnaire.questions.length && this.isDirty();
    isDirty && this.editor.memorizeQuestionnaire(this.questionnaire);
  }

  /**
   * Shows the resume hint when unsaved changes are memorized for the opened questionnaire, or when a new, empty
   * questionnaire is opened. Changes of another questionnaire stay memorized and do not interrupt
   *
   * @param {string} id id of the opened questionnaire, empty for a new one
   */
  private checkMemorizedSession(id?: string) {
    const info = this.editor.getMemorizedQuestionnaireInfo();
    this.pendingSession = info && (!id || info.id === id) ? info : null;
  }

  /**
   * Discards the memorized changes
   */
  public onDiscardSession() {
    this.editor.deleteMemorizedQuestionnaire();
    this.pendingSession = null;
  }

  /**
   * Restores the memorized changes into the editor
   */
  public async onResumeSession() {
    this.pendingSession = null;
    this.layoutService.showPreloader();

    try {
      // keep the snapshot of the loaded questionnaire, so the restored changes count as unsaved
      const snapshot = this.questionnaireSnapshot;
      this.startSession(await this.editor.resumeMemorizedQuestionnaire());
      this.questionnaireSnapshot = snapshot;

      // previous session could contain an already deleted questionnaire. therefore we ask the api if the questionnaire exists
      const existsInDb = this.questionnaire.createdAt ? !!await this.api.hasQuestionnaire(this.questionnaire.id) : false;

      const path = window.location.pathname;
      const replaceValue = existsInDb ? `/editor/${this.questionnaire.id}` : '/editor';
      const newPath = path.replace(/\/editor($|\/.*$)/, replaceValue);
      window.history.pushState(null, null, newPath);

      this.editMode = existsInDb;
      !existsInDb && (this.questionnaire.revision = 1);
    } catch (error) {
      console.error(error);
    }

    setTimeout(() => this.layoutService.hidePreloader(), 300);
  }


  /**
   * Applies license constrictions
   */
  private applyRestrictions() {
    const numQuest = this.configService.get('constraint.numQuestionnaires');

    if (!numQuest) {
      return;
    }

    const filterByUser = this.isSimu;
    this.api.getNumQuestionnaires(filterByUser).then(count => {
      if (count >= numQuest) {
        alert(this._(KEYS.EDITOR.QUESTIONNAIRE_LIMIT_REACHED));
        this.location.back();
      }
    })

    this.editor.loadFiltergroups().then(filtergroups => {
      this.filtergroups = filtergroups;
      const filterGroupsAvailable = this.filtergroups.length;
      const questionnaireHasNoFilterGroups = !(this.questionnaire.meta.tags || []).length;

      if (this.isSimu && filterGroupsAvailable && questionnaireHasNoFilterGroups) {
        const firstFilterGroup = this.filtergroups[0];
        this.questionnaire.meta.tags = [firstFilterGroup.id];
      }
    });
  }

  private applySimuRestrictions() {
    this.editor.loadFiltergroups().then(filtergroups => {
      this.filtergroups = filtergroups;
      const hasNoFiltergroups = this.filterTags(this.questionnaire.meta.tags || []).length === 0;

      if (this.isSimu && hasNoFiltergroups && this.editMode) {
        alert(this._(KEYS.EDITOR.QUESTIONNAIRE_NO_PERMISSION));
        this.location.back();
        return;
      }
    });
  }
}
